import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Gift, Shield, Snowflake, Phone, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { companyInfo } from '../data/mock';

const PromotionsPage = () => {
  return (
    <Layout>
      {/* Header */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 py-10">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Акции и спецпредложения</h1>
          <p className="text-gray-600">Выгодные условия покупки теплиц</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Main Promotion */}
        <div className="bg-gradient-to-br from-emerald-600 to-green-700 rounded-3xl p-8 md:p-12 text-white mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-white/20 hover:bg-white/20 text-white border-0">
              Акция
            </Badge>
            <Badge className="bg-red-500 hover:bg-red-500 text-white border-0">
              До 20 марта
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Закажи теплицу сейчас — получи лучшие условия!
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl">
            При заказе теплицы до 20 марта 2026 — специальные условия для наших клиентов
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Snowflake className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Бесплатное хранение</h3>
              <p className="text-emerald-100">
                Храним вашу теплицу бесплатно до начала сезона. Закажите сейчас — установим когда удобно!
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Фиксация цены</h3>
              <p className="text-emerald-100">
                При заключении договора и оплате — фиксируем цену. Защита от сезонного повышения!
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Gift className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Подарки при заказе</h3>
              <p className="text-emerald-100">
                Специальные бонусы и подарки при заказе теплицы в период акции
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-emerald-200">
            <Clock className="w-5 h-5" />
            <span>Акция действует до 20 марта 2026 года</span>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Как воспользоваться акцией?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-emerald-700 font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2">Выберите теплицу</h3>
              <p className="text-sm text-gray-600">В каталоге или с помощью калькулятора</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-emerald-700 font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2">Оставьте заявку</h3>
              <p className="text-sm text-gray-600">Позвоните или напишите нам</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-emerald-700 font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2">Заключите договор</h3>
              <p className="text-sm text-gray-600">Зафиксируем цену и условия</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-emerald-700 font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2">Получите теплицу</h3>
              <p className="text-sm text-gray-600">Доставим и установим в удобное время</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-amber-50 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Почему выгодно заказать сейчас?</h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="text-emerald-600 font-bold">✓</span>
              <span><strong>Цены зимой ниже</strong> — весной традиционно повышаются на 10-15%</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-600 font-bold">✓</span>
              <span><strong>Гарантированное наличие</strong> — в сезон популярные модели быстро раскупаются</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-600 font-bold">✓</span>
              <span><strong>Удобное время монтажа</strong> — выберите дату установки когда вам удобно</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-600 font-bold">✓</span>
              <span><strong>Без спешки</strong> — спокойно подготовьте участок к началу сезона</span>
            </li>
          </ul>
        </div>

        {/* CTA */}
        <div className="bg-gray-900 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Успейте заказать по выгодной цене!</h2>
          <p className="text-gray-400 mb-6">Акция действует до 20 марта. Свяжитесь с нами прямо сейчас!</p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phoneClean}`}>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 h-14 px-8">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
            <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10 h-14 px-6">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href={companyInfo.social.telegram} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-sky-500 text-sky-400 hover:bg-sky-500/10 h-14 px-6">
                <Send className="w-5 h-5 mr-2" />
                Telegram
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PromotionsPage;
